import { FC, useReducer } from "react";
import { JobApplicationCallbacks, useJobApplication } from "../../../hooks/useJobApplication";
import { VACANCY_CANDIDATE_VISIBILITY, VACANCY_SELECTION_METHOD } from "../../../models/vacancy";
import {
  useAppDispatch as useDispatch,
} from "../../../store";
import {
  showNotificationAction,
} from "../../../store/application/slice";
import { TJobData } from "../../../store/candidate/types";
import CustomButton from "../../UI/Buttons/CustomButton";
import CircleCancel from "../../UI/Icons/CircleCancel";
import RoundedTick from "../../UI/Icons/RoundedTick";
import DeclineApplyJobModal from "../Jobs/AcceptedListing/DeclineApplyJobModal";
import { getCandidateLabel } from "../Jobs/CandidateVacancyCard";
import AcceptJobModal from "../Jobs/JobListings/ApplyJobModal";
import ConfirmApplyJobModal from "../Jobs/JobListings/ConfirmApplyJobModal";
import ConflictConfirmApply from "../Jobs/JobListings/ConflictConfirmApply";
import JobAcceptedConfirmationModal from "../Jobs/JobListings/JobAcceptedConfirmation";
import WithdrawApplyJobModal from "../Jobs/PendingListing/WithdrawApplyJobModal";

interface CandidateJobButtonProps {
  vacancyDetails: any;
  handleView?: (id: number) => void;
  setShowViewModal?: (show: boolean) => void;
  getCandidateJobList?: () => void;
  setShowAcceptedJobs?: (show: boolean) => void;
  onRefresh?: () => void;
  showSingleDayView?: (show: boolean) => void;
  isDocumentsExpired?: boolean;
}

type ModalType = 'accept' | 'confirmApply' | 'withdrawApply' | 'declineApply' | 'conflictConfirm' | 'confirmAccept' | null;

interface ModalState {
  activeModal: ModalType;
}

type ModalAction = 
  | { type: 'OPEN_MODAL'; payload: ModalType }
  | { type: 'CLOSE_MODAL' };

const modalReducer = (state: ModalState, action: ModalAction): ModalState => {
  switch (action.type) {
    case 'OPEN_MODAL':
      return { activeModal: action.payload };
    case 'CLOSE_MODAL':
      return { activeModal: null };
    default:
      return state;
  }
};

const CandidateJobButton: FC<CandidateJobButtonProps> = ({
  vacancyDetails,
  handleView,
  setShowViewModal,
  setShowAcceptedJobs,
  getCandidateJobList,
  onRefresh,
  showSingleDayView,
  isDocumentsExpired
}) => {
  const dispatch = useDispatch();
  const [modalState, modalDispatch] = useReducer(modalReducer, { activeModal: null });
  const { applyForJob, handleJobAccepted } = useJobApplication();

  const callbacks: JobApplicationCallbacks = {
    setShowAcceptedJobs,
    setShowViewModal,
    getCandidateJobList,
    handleView
  };

  /**
   * Handles the complete job application flow including conflicts and restrictions
   */
  const handleJobApplication = async (vacancy: TJobData) => {
    const result = await applyForJob(vacancy);
    
    if (!result.success) {
      if (result.restrictionReached) {
        modalDispatch({ type: 'CLOSE_MODAL' });
        setShowViewModal?.(false);
        setShowAcceptedJobs?.(false);
        dispatch(showNotificationAction("You can't apply more than five jobs in a day."));
        return;
      }
      
      if (result.timeframeConflict) {
        handleTimeframeConflict(result.actionType);
        return;
      }
      return;
    }

    // Handle successful application
    if (result.restrictionReached) {
      dispatch(showNotificationAction("You can't apply more than five jobs in a day."));
      return;
    }

    if (result.actionType === "applied") {
      modalDispatch({ type: 'OPEN_MODAL', payload: 'confirmApply' });
    } else {
      modalDispatch({ type: 'CLOSE_MODAL' });
      // await handleJobAccepted(vacancyDetails, callbacks);
      modalDispatch({ type: 'OPEN_MODAL', payload: 'confirmAccept' });
    }
  };

  /**
   * Handles timeframe conflicts based on action type
   */
  const handleTimeframeConflict = (actionType: string) => {
    if (actionType === "accepted") {
      modalDispatch({ type: 'CLOSE_MODAL' });
      setShowViewModal?.(false);
      setShowAcceptedJobs?.(false);
      dispatch(
        showNotificationAction(
          "You cannot accept this job due to a conflicting timeframe."
        )
      );
    } else {
      modalDispatch({ type: 'OPEN_MODAL', payload: 'conflictConfirm' });
    }
  };

  /**
   * Determines whether to show accept modal or directly apply for job
   */
  const handleConfirmJobApply = (vacancy: TJobData) => {
    const buttonLabel = getCandidateLabel({
      selectionMethod: vacancy.selection_method as VACANCY_SELECTION_METHOD,
      visibilityType: vacancy.visibility_type as VACANCY_CANDIDATE_VISIBILITY,
      visibilityUpgraded: vacancy.visibility_upgraded,
    });

    if (buttonLabel === "Accept" || (vacancy?.candidate_past_jobs_count === 0 && vacancy?.is_elv_centre)) {
      modalDispatch({ type: 'OPEN_MODAL', payload: 'accept' });
    } else {
      handleJobApplication(vacancy);
    }
  };

  /**
   * Opens withdraw application modal
   */
  const handleWithdrawJobApplication = () => {
    modalDispatch({ type: 'OPEN_MODAL', payload: 'withdrawApply' });
  };

  /**
   * Opens decline application modal
   */
  const handleDeclineJobApplication = () => {
    modalDispatch({ type: 'OPEN_MODAL', payload: 'declineApply' });
  };

  /**
   * Closes all modals
   */
  const closeModal = () => {
    modalDispatch({ type: 'CLOSE_MODAL' });
  };

  /**
   * Gets the button label based on vacancy details
   */
  const getButtonLabel = () => {
    return getCandidateLabel({
      selectionMethod: vacancyDetails.selection_method as VACANCY_SELECTION_METHOD,
      visibilityType: vacancyDetails.visibility_type as VACANCY_CANDIDATE_VISIBILITY,
      visibilityUpgraded: vacancyDetails.visibility_upgraded,
    });
  };

  /**
   * Renders the appropriate button based on submission type
   */
  const renderActionButton = () => {
    if (vacancyDetails.submit_type === "accepted") {
      return (
        <CustomButton
          variant={"primary"}
          type="button"
          className="btn-theme bg-black text-white max-h-30px w-100"
          onClick={handleDeclineJobApplication}
        >
          <CircleCancel fill="white" />
          Decline
        </CustomButton>
      );
    }

    if (vacancyDetails.submit_type === "applied") {
      return (
        <CustomButton
          variant={"primary"}
          type="button"
          className="btn-theme bg-black text-white max-h-30px w-100"
          onClick={handleWithdrawJobApplication}
        >
          <CircleCancel fill="white" />
          Withdraw
        </CustomButton>
      );
    }

    return (
      <CustomButton
        variant={"primary"}
        type="button"
        className="btn-theme bg-teal text-black max-h-30px w-100"
        onClick={() => handleConfirmJobApply(vacancyDetails)}
        disabled={isDocumentsExpired}
      >
        <RoundedTick color={isDocumentsExpired ? "#aaaaaa" : "black"} />
        {getButtonLabel()}
      </CustomButton>
    );
  };

  return (
    <>
      {/* Accept Job Modal */}
      <AcceptJobModal
        showApplyJobModal={modalState.activeModal === 'accept'}
        setShowApplyJobModal={closeModal}
        vacancyData={vacancyDetails}
        handleApplyForJob={handleJobApplication}
      />

      {/* Confirm Apply Job Modal */}
      <ConfirmApplyJobModal
        showApplyJobModal={modalState.activeModal === 'confirmApply'}
        setShowApplyJobModal={closeModal}
        vacancyData={vacancyDetails}
        handleApplyForJob={handleJobApplication}
        setShowViewModal={setShowViewModal}
        handleView={handleView}
        centreId={vacancyDetails?.centre_id}
        showSingleDayView={showSingleDayView}
      />

      <JobAcceptedConfirmationModal
        showApplyJobModal={modalState.activeModal === 'confirmAccept'}
        setShowApplyJobModal={closeModal}
        vacancyData={vacancyDetails}
        handleApplyForJob={handleJobApplication}
        setShowViewModal={setShowViewModal}
        handleView={handleView}
        centreId={vacancyDetails?.centre_id}
        showSingleDayView={showSingleDayView}
      />

      {/* Withdraw Apply Job Modal */}
      <WithdrawApplyJobModal
        showApplyJobModal={modalState.activeModal === 'withdrawApply'}
        setShowApplyJobModal={closeModal}
        vacancyData={vacancyDetails}
        handleApplyForJob={handleJobApplication}
        setShowViewModal={setShowViewModal}
        showSingleDayView={showSingleDayView}
        getCandidateJobList={getCandidateJobList}
        onRefresh={onRefresh}
      />

      {/* Decline Apply Job Modal */}
      <DeclineApplyJobModal
        showApplyJobModal={modalState.activeModal === 'declineApply'}
        setShowApplyJobModal={closeModal}
        vacancyData={vacancyDetails}
        handleApplyForJob={handleJobApplication}
        getCandidateJobList={getCandidateJobList}
        showSingleDayView={showSingleDayView}
        setShowViewModal={setShowViewModal}
      />

      {/* Conflict Confirm Apply Modal */}
      <ConflictConfirmApply
        showApplyJobModal={modalState.activeModal === 'conflictConfirm'}
        setShowApplyJobModal={closeModal}
        vacancyData={vacancyDetails}
        handleApplyForJob={handleJobApplication}
        showConfirmApplyJobModal={modalState.activeModal === 'confirmApply'}
        setShowConfirmApplyJobModal={() => modalDispatch({ type: 'OPEN_MODAL', payload: 'confirmApply' })}
        showConflictConfirmModal={modalState.activeModal === 'conflictConfirm'}
        setShowConflictConfirmModal={() => modalDispatch({ type: 'OPEN_MODAL', payload: 'conflictConfirm' })}
      />

      {/* Action Button */}
      {renderActionButton()}
    </>
  );
};

export default CandidateJobButton;